import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  useTheme,
  Theme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { StatusType } from "../../../../../models/Shipments";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";

interface DropdownProps {
  open: boolean;
  data: StatusType[];
  onStatusDropdownSelect: (id: number) => void;
  onClose: () => void;
}

const ShipmentStatusPopup: React.FC<DropdownProps> = ({
  open,
  data,
  onStatusDropdownSelect,
  onClose,
}) => {
  const theme = useTheme<Theme>();
  const [status, setStatus] = useState<number>(-1);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (open) {
      setStatus(-1);
      setError(false);
    }
  }, [open]);

  const statusOptions: FilterValue[] = data.map((item) => ({
    id: String(item.id),
    value: item.value,
  }));

  const selectedStatus: FilterValue[] =
    status !== -1
      ? [
          {
            id: String(status),
            value: data.find((item) => item.id === status)?.value || "",
          },
        ]
      : [];

  const handleSave = () => {
    try {
      if (status === -1) {
        setError(true);
        return;
      }
      onStatusDropdownSelect(status);
      onClose();
    } catch {
      setError(true);
    }
  };

  const handleCancel = () => {
    setStatus(-1);
    setError(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors?.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors?.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            background: `linear-gradient(135deg, ${theme.palette.customColors?.white || "#fff"} 0%, ${theme.palette.customColors?.lightGreyColor || "#f5f5f5"} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors?.blueGradientStart || "#1976d2"} 0%, ${theme.palette.customColors?.blueGradientEnd || "#42a5f5"} 50%, ${theme.palette.customColors?.blueGradientStart || "#1976d2"} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px", sm: "16px 20px" },
            borderBottom: `1px solid ${theme.palette.customColors?.greyBorder || "#e0e0e0"}`,
          }}
        >
          <CustomTypography variant="heading4">Update Status</CustomTypography>
          <IconButton
            onClick={handleCancel}
            sx={{
              color: theme.palette.customColors?.greyTextColor || "#666",
              "&:hover": {
                backgroundColor: theme.palette.customColors?.lightGreyColor,
                color: theme.palette.customColors?.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
          }}
        >
          <CustomTypography
            variant="body2"
            color={theme.palette.customColors?.greyTextColor}
            style={{ marginBottom: 16 }}
          >
            Select a new status to apply to the selected shipments.
          </CustomTypography>

          <CustomTypography
            variant="body2"
            sx={{ mb: 0.75, fontWeight: 500 }}
          >
            Status
          </CustomTypography>
          <AppSearchDropdownMulti
            isMultiSelect={false}
            title="Select Status"
            local
            list={statusOptions}
            value={selectedStatus}
            handleSelect={(selectedItems) => {
              setError(false);
              setStatus(
                selectedItems.length > 0 ? Number(selectedItems[0].id) : -1,
              );
            }}
            borderColor={
              error
                ? "#d32f2f"
                : theme.palette.customColors?.greyBorder || "#ddd"
            }
            error={error}
            errorMessage="Please select a status"
            inputHeight="40px"
            borderRadius="4px"
          />
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${theme.palette.customColors?.greyBorder || "#e0e0e0"}`,
            backgroundColor: theme.palette.customColors?.lightGreyColor,
            gap: 2,
          }}
        >
          <AppButton
            variant="grayscale"
            label="Cancel"
            onClick={handleCancel}
          />
          <AppButton
            variant="primary"
            label="Save"
            onClick={handleSave}
            disabled={status === -1}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ShipmentStatusPopup;
